import {
  DEFAULT_STOREFRONT_TEMPLATE_ID,
  FORGE_MAX_CREATOR_CUT_PERCENT,
  type PageTemplateChoices,
  resolveStorefrontFeatures,
  resolveStorefrontTerminology,
  type StorefrontFeatureKey,
  storefrontTemplateIdSchema,
  type TenantModules,
} from "@loot-os/api-contract";
import { useMemo } from "react";

import { useCurrency } from "./currency";
import { useStorefrontConfig } from "./storefront";
import type { PreviewState } from "./themePreview";
import { useTenantModules } from "./useTenantModules";
import { useTenantName } from "./useTenantName";

/**
 * Everything the theme preview draws that is the store's own, apart from the theme and the page:
 * its name and logo, currency, the inputs of its default copy, the Forge's commission, and its
 * saved layouts (the storefront template and each page's own pick). Read the way the storefront
 * reads them (the tenant's name without a display name, a game off while staff have its module
 * off, the forge settings' defaults and the default template until the config loads), so every
 * page that frames the preview (the Theme tab, the Pages page) shows the same store.
 */
export function useThemePreviewInputs(): Pick<
  PreviewState,
  "brand" | "currency" | "store" | "template" | "pageTemplates"
> {
  const stored = useStorefrontConfig().query.data?.config;
  const currency = useCurrency();
  const tenantName = useTenantName();
  const modules = useTenantModules();

  const brand = useMemo(
    () => ({ name: stored?.displayName || tenantName, logoUrl: stored?.logoUrl ?? "" }),
    [stored?.displayName, tenantName, stored?.logoUrl],
  );
  const store = useMemo(
    () => ({
      terms: resolveStorefrontTerminology(stored?.terminology ?? {}),
      features: withModules(resolveStorefrontFeatures(stored?.features), modules),
      socialLinks: stored?.socialLinks ?? {},
      // As core resolves `forge_settings`: commission on at the platform's maximum, and no
      // images, unless set.
      forge: {
        commissionEnabled: stored?.forgeSettings.commissionEnabled ?? true,
        maxCreatorCutPercent:
          stored?.forgeSettings.maxCreatorCutPercent ?? FORGE_MAX_CREATOR_CUT_PERCENT,
        boxImages: stored?.forgeSettings.boxImages ?? [],
      },
    }),
    [stored?.terminology, stored?.features, stored?.socialLinks, stored?.forgeSettings, modules],
  );
  const parsed = storefrontTemplateIdSchema.safeParse(stored?.storefrontTemplate);
  const template = parsed.success ? parsed.data : DEFAULT_STOREFRONT_TEMPLATE_ID;
  const pageTemplates = stored?.pageTemplates ?? NO_PICKS;

  return { brand, currency, store, template, pageTemplates };
}

/** No saved picks yet: one object, so the preview posts again only once picks arrive. */
const NO_PICKS: PageTemplateChoices = {};

/**
 * A store's game switches with the tenant's modules applied: a module off turns its game off, as
 * core's `applyTenantModules` does for the storefront's resolved config.
 */
function withModules(
  features: Record<StorefrontFeatureKey, boolean>,
  modules: TenantModules | null,
): Record<StorefrontFeatureKey, boolean> {
  if (!modules) return features;
  return {
    ...features,
    battles: features.battles && modules.battles,
    chat: features.chat && modules.chat,
    forge: features.forge && modules.forge,
  };
}
